/**
 * Custom Variant Picker
 * Handles variant selection and updates product form
 */

class CustomVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Find all variant cards
    this.variantCards = this.querySelectorAll('.variant-card');

    // Add click event listeners to variant cards
    this.variantCards.forEach((card) => {
      card.addEventListener('click', this.onVariantChange.bind(this));
    });
  }

  onVariantChange(event) {
    const card = event.currentTarget;
    const radio = card.querySelector('input[type="radio"]');

    if (!radio) return;

    const variantId = radio.dataset.variantId;
    const variantTitle = radio.dataset.variantTitle || card.querySelector('.variant-title')?.textContent?.trim() || '';

    // Update selected state on cards
    this.variantCards.forEach((c) => c.classList.remove('selected'));
    card.classList.add('selected');

    // Check the radio button
    radio.checked = true;

    // Get product info element
    const productInfo = this.closest('product-info');

    // Update the product title IMMEDIATELY (before fetch)
    if (productInfo && variantTitle) {
      const titleElement = productInfo.querySelector('.product__title h1');
      if (titleElement) {
        titleElement.textContent = variantTitle;
      }
    }

    // Note: Variant description comes from metafield and is only available via fetch
    // It will be updated in updateVariantElements() when the fetch completes

    // Check if multi-variant images is enabled
    const mediaGallery = productInfo?.querySelector('media-gallery[data-enable-multi-variant-images]');
    const hasMultiVariantImages = !!mediaGallery;

    // Dispatch custom event for multi-variant images
    if (hasMultiVariantImages) {
      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: {
            variant: {
              id: variantId,
              title: variantTitle,
            },
          },
        })
      );
    }

    // Dispatch a generic variant change event for other UI (e.g., variant video)
    document.dispatchEvent(
      new CustomEvent('product:variant-changed', {
        detail: {
          variant: {
            id: variantId,
            title: variantTitle,
          },
        },
      })
    );

    // Trigger a full product section update by fetching with the new variant
    this.updateProductSection(variantId, hasMultiVariantImages);
  }

  updateProductSection(variantId, hasMultiVariantImages = false) {
    const productInfo = this.closest('product-info');
    if (!productInfo) return;

    // Get product URL and section ID
    const productUrl = productInfo.dataset.url;
    const sectionId = productInfo.dataset.section;

    if (!productUrl || !sectionId) return;

    // Build the URL to fetch the product section with the new variant
    const url = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;

    // Show loading state (optional)
    this.classList.add('loading');

    // Fetch the updated product section
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductInfo = html.querySelector('product-info');

        if (newProductInfo) {
          // Update various parts of the product info
          this.updateVariantElements(productInfo, newProductInfo, hasMultiVariantImages);

          // Update the URL
          window.history.replaceState({}, '', `${productUrl}?variant=${variantId}`);

          // Update the form's variant input
          this.updateVariantInput(productInfo, variantId);
        }
      })
      .catch((error) => {
        console.error('Error updating variant:', error);
      })
      .finally(() => {
        this.classList.remove('loading');
      });
  }

  updateVariantElements(currentProductInfo, newProductInfo, hasMultiVariantImages = false) {
    // Note: Title is updated immediately in onVariantChange() for instant feedback
    // This function handles updates that require the fetch response (description, price, SKU, etc.)

    // Update variant description
    const currentVariantDesc = currentProductInfo.querySelector('.variant__description-meta');
    const newVariantDesc = newProductInfo.querySelector('.variant__description-meta');

    if (currentVariantDesc && newVariantDesc) {
      currentVariantDesc.innerHTML = newVariantDesc.innerHTML;
    } else if (currentVariantDesc && !newVariantDesc) {
      currentVariantDesc.remove();
    } else if (!currentVariantDesc && newVariantDesc) {
      // Insert new variant description in the right place
      const variantHeading = currentProductInfo.querySelector('.variant-title-heading');
      if (variantHeading) {
        variantHeading.insertAdjacentElement('beforebegin', newVariantDesc.cloneNode(true));
      }
    }

    // Update price
    const sectionId = currentProductInfo.dataset.section;
    const currentPrice = currentProductInfo.querySelector(`#price-${sectionId}`);
    const newPrice = newProductInfo.querySelector(`#price-${sectionId}`);

    if (currentPrice && newPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }

    // Update SKU
    const currentSku = currentProductInfo.querySelector(`#Sku-${sectionId}`);
    const newSku = newProductInfo.querySelector(`#Sku-${sectionId}`);

    if (currentSku && newSku) {
      currentSku.innerHTML = newSku.innerHTML;
      currentSku.classList.toggle('hidden', newSku.classList.contains('hidden'));
    }

    // Update media gallery
    if (!hasMultiVariantImages) {
      // Full gallery update (default behavior)
      this.updateMediaGallery(currentProductInfo, newProductInfo);
    }
    // When multi-variant-images is enabled, skip all media gallery updates
    // The multi-variant-images.js handles everything via the variant:changed event

    // Update variant benefits (metafield)
    const currentVariantBenefits = currentProductInfo.querySelector('.variant-benefits');
    const newVariantBenefits = newProductInfo.querySelector('.variant-benefits');

    if (currentVariantBenefits && newVariantBenefits) {
      currentVariantBenefits.innerHTML = newVariantBenefits.innerHTML;
    } else if (currentVariantBenefits && !newVariantBenefits) {
      currentVariantBenefits.remove();
    } else if (!currentVariantBenefits && newVariantBenefits) {
      // Insert before the first accordion (shipping_returns is always present)
      const firstAccordion = currentProductInfo.querySelector('.product__accordion');
      firstAccordion?.insertAdjacentElement('beforebegin', newVariantBenefits.cloneNode(true));
    }

    // Update product details accordion (metafield)
    const currentProductDetails = currentProductInfo
      .querySelector('[id*="ProductDetails-"]')
      ?.closest('.product__accordion');
    const newProductDetails = newProductInfo.querySelector('[id*="ProductDetails-"]')?.closest('.product__accordion');

    if (currentProductDetails && newProductDetails) {
      const currentContent = currentProductDetails.querySelector('.accordion__content');
      const newContent = newProductDetails.querySelector('.accordion__content');
      if (currentContent && newContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    } else if (currentProductDetails && !newProductDetails) {
      currentProductDetails.remove();
    } else if (!currentProductDetails && newProductDetails) {
      // Insert before shipping_returns accordion
      const shippingReturns = currentProductInfo
        .querySelector('[id*="ShippingReturns-"]')
        ?.closest('.product__accordion');
      shippingReturns?.insertAdjacentElement('beforebegin', newProductDetails.cloneNode(true));
    }
  }

  updateMediaGallery(currentProductInfo, newProductInfo) {
    // Get the media gallery UL elements (not the media-gallery custom element)
    const currentGalleryList = currentProductInfo.querySelector('media-gallery ul');
    const newGalleryList = newProductInfo.querySelector('media-gallery ul');

    if (!currentGalleryList || !newGalleryList) return;

    // Helper function to refresh the data structures after DOM changes
    const refreshData = () => {
      const items = Array.from(currentGalleryList.querySelectorAll('li[data-media-id]'));
      const idSet = new Set(items.map((item) => item.dataset.mediaId));
      const idMap = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
      return [items, idSet, idMap];
    };

    // Get initial state
    let [currentItems, currentIdSet, currentIdMap] = refreshData();
    const newItems = Array.from(newGalleryList.querySelectorAll('li[data-media-id]'));
    const newIdSet = new Set(newItems.map((item) => item.dataset.mediaId));

    let shouldRefresh = false;

    // Add items from new gallery that don't exist in current gallery
    for (let i = newItems.length - 1; i >= 0; i--) {
      const newItemId = newItems[i].dataset.mediaId;
      if (!currentIdSet.has(newItemId)) {
        const clonedItem = newItems[i].cloneNode(true);
        currentGalleryList.prepend(clonedItem);
        shouldRefresh = true;
      }
    }

    // Remove items from current gallery that don't exist in new gallery
    for (let i = 0; i < currentItems.length; i++) {
      const currentItemId = currentItems[i].dataset.mediaId;
      if (!newIdSet.has(currentItemId)) {
        currentItems[i].remove();
        shouldRefresh = true;
      }
    }

    // Refresh data if we made changes
    if (shouldRefresh) {
      [currentItems, currentIdSet, currentIdMap] = refreshData();
    }

    // Reorder items to match the new gallery order
    newItems.forEach((newItem, newIndex) => {
      const itemData = currentIdMap.get(newItem.dataset.mediaId);
      if (itemData && itemData.index !== newIndex) {
        const targetPosition = currentGalleryList.querySelector(`li:nth-of-type(${newIndex + 1})`);
        currentGalleryList.insertBefore(itemData.item, targetPosition);
        [currentItems, currentIdSet, currentIdMap] = refreshData();
      }
    });

    // Get the featured media ID (first item in new gallery)
    const featuredMediaId = newItems[0]?.dataset.mediaId;
    if (!featuredMediaId) return;

    // Use Shopify's native setActiveMedia to update UI
    const mediaGallery = currentProductInfo.querySelector('media-gallery');
    if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(featuredMediaId, true);
    }

    // Update thumbnails gallery as well
    this.updateThumbnails(currentProductInfo, newProductInfo);
  }

  updateThumbnails(currentProductInfo, newProductInfo) {
    const currentThumbnailList = currentProductInfo.querySelector('.thumbnail-list');
    const newThumbnailList = newProductInfo.querySelector('.thumbnail-list');

    if (!currentThumbnailList || !newThumbnailList) return;

    // Helper function to refresh thumbnail data
    const refreshThumbnailData = () => {
      const items = Array.from(currentThumbnailList.querySelectorAll('li[data-target]'));
      const idSet = new Set(items.map((item) => item.dataset.target));
      const idMap = new Map(items.map((item, index) => [item.dataset.target, { item, index }]));
      return [items, idSet, idMap];
    };

    let [currentThumbs, currentThumbIdSet, currentThumbIdMap] = refreshThumbnailData();
    const newThumbs = Array.from(newThumbnailList.querySelectorAll('li[data-target]'));
    const newThumbIdSet = new Set(newThumbs.map((item) => item.dataset.target));

    let shouldRefresh = false;

    // Add missing thumbnails
    for (let i = newThumbs.length - 1; i >= 0; i--) {
      const thumbId = newThumbs[i].dataset.target;
      if (!currentThumbIdSet.has(thumbId)) {
        const clonedThumb = newThumbs[i].cloneNode(true);
        currentThumbnailList.prepend(clonedThumb);
        shouldRefresh = true;
      }
    }

    // Remove old thumbnails
    for (let i = 0; i < currentThumbs.length; i++) {
      const thumbId = currentThumbs[i].dataset.target;
      if (!newThumbIdSet.has(thumbId)) {
        currentThumbs[i].remove();
        shouldRefresh = true;
      }
    }

    // Refresh and reorder
    if (shouldRefresh) {
      [currentThumbs, currentThumbIdSet, currentThumbIdMap] = refreshThumbnailData();
    }

    // Reorder thumbnails to match new order
    newThumbs.forEach((newThumb, newIndex) => {
      const thumbData = currentThumbIdMap.get(newThumb.dataset.target);
      if (thumbData && thumbData.index !== newIndex) {
        const targetPosition = currentThumbnailList.querySelector(`li:nth-of-type(${newIndex + 1})`);
        currentThumbnailList.insertBefore(thumbData.item, targetPosition);
        [currentThumbs, currentThumbIdSet, currentThumbIdMap] = refreshThumbnailData();
      }
    });
  }

  updateVariantInput(productInfo, variantId) {
    const sectionId = productInfo.dataset.section;
    const forms = productInfo.querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}`);

    forms.forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

// Register the custom element
if (!customElements.get('custom-variant-picker')) {
  customElements.define('custom-variant-picker', CustomVariantPicker);
}
